import { isInteger } from "@foretagsplatsen/core-helpers/number";
import { isNull } from "./object-util.js";
import dompurify from "dompurify";
import latinMap from "../latinMap.js";
import numbro from "numbro";

const placeholder = /{(\d+)}/g;

/**
 * Check if the provided object is a string.
 *
 * @param {any} object - The object to check
 * @return {boolean}
 */
export function isString(object) {
	return typeof object === "string";
}

/**
 * Check if the provided object is a RegExp.
 *
 * @param {any} object - The object to check
 * @return {boolean}
 */
export function isRegExp(object) {
	return Object.prototype.toString.call(object) === "[object RegExp]";
}

/**
 * Replace placeholders in a string with the provided arguments.
 * The placeholders are defined as `{0}`, `{1}`, etc.
 *
 * @param {string} formatString - The string to format
 * @param {...string} args - The arguments to replace the placeholders with
 * @return {string} The formatted string
 */
export function format(formatString, ...args) {
	return formatString.replace(placeholder, (match, number) =>
		args[number] === undefined ? match : args[number],
	);
}

/**
 * Check if the provided string is null, undefined or empty.
 *
 * @param {string} string - The string to check
 * @return {boolean}
 */
export function isNullOrEmpty(string) {
	return isNull(string) || string === "";
}

// From http://stackoverflow.com/questions/46155/validate-email-address-in-javascript#46181
const emailRegex =
	// eslint-disable-next-line sonarjs/regex-complexity -- this is copy/pasted
	/^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@(((?:\d{1,3}\.){3}\d{1,3})|(([\dA-Za-z-]+\.)+[A-Za-z]{2,}))$/;

// From https://urlregex.com/
const urlRegex =
	// eslint-disable-next-line sonarjs/regex-complexity,sonarjs/slow-regex, sonarjs/empty-string-repetition -- this is copy/pasted
	/((([A-Za-z]{3,9}:(?:\/\/)?)?(?:[\w$&+,:;=-]+@)?[\d.A-Za-z-]+|(?:www\.|[\w$&+,:;=-]+@)[\d.A-Za-z-]+)((?:\/[\w%+./~-]*)?\??[\w%&+.;=@-]*#?[\w!./\\]*)?)/;

const protocolRegex = /^([A-Za-z]{3,9}:(?:\/\/)?)/;

/**
 * Check if the provided string is a valid email address.
 *
 * @param {string} string - The string to check
 * @return {boolean}
 */
export function isValidEmailAddress(string) {
	return emailRegex.test(string);
}

/**
 * Check if the provided string is a valid URL.
 *
 * @param {string} string - The string to check
 * @return {boolean}
 */
export function isValidUrl(string) {
	return urlRegex.test(string);
}

/**
 * Check if the provided string has a protocol.
 *
 * @param {string} string - The string to check
 * @return {boolean}
 */
export function hasProtocol(string) {
	return protocolRegex.test(string);
}

/**
 * Capitalize the first letter of a string.
 *
 * @param {string} string - The string to capitalize
 * @return {string}
 */
export function capitalize(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}

function uncapitalize(string) {
	return string.charAt(0).toLowerCase() + string.slice(1);
}

/**
 * Convert a string to camel case.
 *
 * @param {string} string - The string to convert
 * @return {string}
 */
export function toCamelCase(string) {
	// words consists of letters and numbers, the rest being separators:
	const wordSeparator = /[\W_]+/;

	const words = string.split(wordSeparator).filter(Boolean);

	if (words.length === 0) {
		return "";
	}

	const camelCaseWords = [
		uncapitalize(words[0]),
		...words.slice(1).map(capitalize),
	];

	return camelCaseWords.join("");
}

/**
 * Transform a string in kebab case. It first transforms it to camel
 * case to be sure it handles correctly space, dash or dots separated
 * inputs.
 */
export function toKebabCase(string) {
	return toCamelCase(string).replace(
		/[A-Z]/g,
		(capitalLetter) => `-${capitalLetter.toLowerCase()}`,
	);
}

/**
 * Convert a property name to a getter name.
 * For instance, `foo` will be converted to `getFoo`.
 *
 * @param {string} propName - The property name to convert
 * @return {string}
 */
export function toGetterName(propName) {
	return `get${capitalize(propName)}`;
}

// From http://stackoverflow.com/questions/286921/efficiently-replace-all-accented-characters-in-a-string#9667817
/**
 * Convert a string to a string without accents.
 *
 * @param {string} string - The string to convert
 * @return {string}
 */
export function latinize(string) {
	return string.replace(/[^\d A-Z[\]a-z]/g, (a) => latinMap[a] || a);
}

/**
 * Remove the BOM from a string, which IE11 cannot handle.
 *
 * @param {string} string - The string to remove the BOM from
 * @return {string}
 */
export function stripBom(string) {
	// remove utf-8 BOM, which IE11 cannot handle
	// Fix FTGP-7240.
	if (string.charCodeAt(0) === 0xfe_ff) {
		return string.substring(1);
	}
	return string;
}

/**
 * Return a new string based on `string` that is formula-escaped for
 * Excel.
 *
 * If the first character of the string is =,-,+, or @, prefix the string
 * with a single quote.
 */
export function excelFormulaEscaped(string) {
	if (!isString(string)) {
		return string;
	}
	if (!"=-+@".includes(string[0])) {
		return string;
	}

	return `'${string}`;
}

/**
 * Sanitize a string to be used in a WYSIWYG editor.
 *
 * @param {string} string - The string to sanitize
 * @return {string}
 */
export function sanitizeWysiwygEditorHtml(string) {
	return dompurify.sanitize(string, {
		ALLOWED_TAGS: [
			"a",
			"b",
			"blockquote",
			"u",
			"br",
			"h2",
			"h3",
			"i",
			"li",
			"ol",
			"p",
			"span",
			"strike",
			"ul",
			"strong",
			"s",
			"del",
			"em",
			"div",
		],
		ALLOWED_ATTR: ["style", "href", "target", "rel"],
		ADD_ATTR: ["imageplugin-src", "imageplugin-alt"],
	});
}

/**
 * Generate a GUID.
 *
 * @return {string}
 */
export function guid() {
	function _p8(s) {
		// eslint-disable-next-line sonarjs/pseudo-random -- don't want to change that now
		const p = `${Math.random().toString(16)}000000000`.substring(2, 10);
		return s ? `-${p.substring(0, 4)}-${p.substring(4, 8)}` : p;
	}

	return _p8() + _p8(true) + _p8(true) + _p8();
}

/**
 * Check if the provided string is an integer.
 *
 * @param {string} string - The string to check
 * @return {boolean}
 */
export function isStringInteger(string) {
	const number = numbro.unformat(string);
	return isInteger(number);
}
